<template>
    <div class="revising-editor-save-actions">
        <button type="button" class="revising-editor-save-actions__save-button"
            :disabled="isSaving || isApplying || !hasUnsavedChanges" @click="onClickSave">
            {{ tempSaveLabel }}
        </button>
        <button type="button" class="revising-editor-save-actions__apply-button"
            :disabled="isApplying || isSaving" @click="onClickApply">
            {{ applyLabel }}
        </button>
    </div>
</template>

<script lang="ts" setup>
import { computed, defineEmits, defineProps, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n-bridge';
import ProofreadingApplyConfirmDialog, {
    ProofreadingApplyConfirmDialogResult,
} from '@/components/ui/dialogs/ProofreadingApplyConfirmDialog.vue';
import { Dialog } from '@/lib/utils/dialog';

const props = defineProps<{
    novelId: string;
    isSaving: boolean;
    isApplying: boolean;
    hasUnsavedChanges: boolean;
    showSaveCompleted: boolean;
    applyConfirmHandler: () => Promise<void>;
}>();

const emit = defineEmits<{
    (event: 'save-click'): void;
}>();

const { t } = useI18n();
const instance = getCurrentInstance();

const tempSaveLabel = computed(() => {
    if (props.isSaving) {
        return t('proofreadingReviseManuscriptPage.saving');
    }
    if (props.showSaveCompleted) {
        return t('proofreadingReviseManuscriptPage.tempSaveCompleted');
    }
    return t('proofreadingReviseManuscriptPage.tempSave');
});

const applyLabel = computed(() => (
    props.isApplying
        ? t('proofreadingReviseManuscriptPage.applyingToManuscript')
        : t('proofreadingReviseManuscriptPage.applyToManuscript')
));

function onClickSave() {
    emit('save-click');
}

async function onClickApply() {
    if (props.isApplying || props.isSaving) return;

    const dialog = new Dialog(ProofreadingApplyConfirmDialog);
    const result = (await dialog.show({
        onConfirm: props.applyConfirmHandler,
    })) as ProofreadingApplyConfirmDialogResult | undefined;

    if (!result?.confirmed) return;

    instance?.proxy?.$router.push({
        name: 'proofreadingSelectType',
        params: { novelId: props.novelId },
    });
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.revising-editor-save-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 12px;

    &__save-button,
    &__apply-button {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 120px;
        padding: 10px 24px;
        border: none;
        border-radius: var(--radius-rounded);
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-bold);
        white-space: nowrap;
        cursor: pointer;
        overflow: visible;

        @include hover-overlay;

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    &__save-button {
        background-color: var(--color-surface-base);
        color: var(--color-text-normal);
    }

    &__apply-button {
        background-color: var(--color-primary);
        color: var(--color-text-invert);
    }
}
</style>
